import * as React from 'react';
import {connect} from 'react-redux';

import {reduxForm, formValueSelector, getFormSyncErrors, SubmissionError} from 'redux-form';
// @ts-expect-error
import type {FormProps} from 'redux-form/lib/types.js.flow';
import _ from 'underscore';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {GraphQLErrorObject} from 'nutshell-core/utils/graphql-errors';

import {SaveButton} from 'shells/button/save-button/save-button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {Dialog} from 'shells/dialog/dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {FooterBar} from 'shells/modal/footer-bar';

import type {
    SessionUser as SessionUserFragment,
    RelatedEmailAddresses as RelatedEmailAddressesFragment,
    EmailableLead,
    GetSuggestedReplies_suggestedReplies as SuggestedReplies,
} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../../lead/refetch-helpers';
import {accountRefetchForEvent} from '../../../peep/account/hooks/use-account-update-refetch';
import {contactRefetchForEvent} from '../../../peep/contact/hooks/use-contact-update-refetch';

import type {NutshellState} from '../../../../store';

import {selectSequenceId, selectTemplateId} from '../../email-composer-actions';
import {getSelectedSequenceId, getSelectedTemplateId} from '../../email-composer-selectors';
import {getSuggestedEmailReplyId} from '../../email-reply-selectors';

import {createFromScratchTemplate} from '../create-from-scratch-template';
import {EmailComposerForm} from '../email-composer-form';
import {PLACEHOLDER_DISABLE_TEXT} from '../fields/email-form-section';
import {getTemplateFormStateName} from '../get-template-form-state-name';
import {FORM_NAME, CREATE_FROM_SCRATCH_ID} from '../types';
import type {
    EmailComposerFormStateNew,
    EmailComposerFormSequenceState,
    OnSubmitArgs,
} from '../types';

import {TemplateSidebar} from './template-sidebar';

import styles from '../email-sender-form.module.css';

const valueSelector = formValueSelector(FORM_NAME);
const SYNC_WARNING =
    'Email sync is not enabled, so Nutshell won’t be able to stop sending automatic follow-ups if your recipient replies. Are you sure you want to continue?';
export const DELAY_ERROR = 'DELAY_VALIDATION_ERROR';

type OwnProps = {
    relatedEmailAddresses: RelatedEmailAddressesFragment;
    relatedLeads: EmailableLead[];
    user: SessionUserFragment;
    onSubmit: (arg1: OnSubmitArgs) => Promise<any>;
    isLoadingTemplates: boolean;
    templateListErrorMessage: string | null | undefined;
    closeModal: () => void;
    hideTemplates?: boolean;
    searchValue: string;
    setSearchValue: (searchValue: string) => void;
    onEmailEdited: () => void;
    emailReplyId?: string;
    isBodyFocused?: boolean;
    suggestedReplies?: SuggestedReplies;
    isLoadingSuggestedReplies?: boolean;
    replyQuotedSuffixHtml?: string | null;
};

type StateProps = {
    sequences: {
        [sequenceId: string]: EmailComposerFormSequenceState;
    };
    isSyncEnabled: boolean;
    isReply: boolean;
    selectedSequenceId: string;
    selectedTemplateId: string;
    syncErrors: any;
    suggestedEmailReplyId?: string;
};

type DispatchProps = {
    selectSequenceId: (sequenceId: string) => void;
    selectTemplateId: (templateId: string) => void;
};

type OwnAndFormProps = OwnProps & FormProps;

type Props = OwnAndFormProps & StateProps & DispatchProps;

type State = {
    isWarningAboutSyncedEmail: boolean;
    shouldShowPlaceholderWarning: boolean;
    hasAcknowledgedPlaceholderWarning: boolean;
    shouldSaveAsTemplate: boolean;
    emailIdToSaveAsTemplate: string | null | undefined;
    temporarySubject: string | null | undefined;
    temporaryPlaintextBody: string | null | undefined;
    temporaryHtmlBody: string | null | undefined;
    hasLoaded: boolean;
    isShowingDelayError: boolean;
};

const mapStateToProps = (state: NutshellState): StateProps => ({
    sequences: valueSelector(state, 'sequences'),
    isSyncEnabled: valueSelector(state, 'fromAddress.isSyncEnabled'),
    isReply: Boolean(valueSelector(state, 'reply')),
    selectedSequenceId: getSelectedSequenceId(state),
    selectedTemplateId: getSelectedTemplateId(state),
    syncErrors: getFormSyncErrors(FORM_NAME)(state),
    suggestedEmailReplyId: getSuggestedEmailReplyId(state),
});

// @ts-expect-error
const mapDispatchToProps = (dispatch) => ({
    // @ts-expect-error
    selectSequenceId: (sequenceId) => {
        dispatch(selectSequenceId(sequenceId));
    },
    // @ts-expect-error
    selectTemplateId: (templateId) => {
        dispatch(selectTemplateId(templateId));
    },
});

class EmailSenderFormComponent extends React.PureComponent<Props, State> {
    saveButton: HTMLElement | null | undefined;

    constructor(props: Props) {
        super(props);
        this.state = {
            isWarningAboutSyncedEmail: false,
            shouldShowPlaceholderWarning: false,
            hasAcknowledgedPlaceholderWarning: false,
            shouldSaveAsTemplate: false,
            emailIdToSaveAsTemplate: null,
            temporarySubject: null,
            temporaryHtmlBody: null,
            temporaryPlaintextBody: null,
            hasLoaded: false,
            isShowingDelayError: false,
        };

        // Avoids rendering one time with undefined initial values, which we have been
        // seeing occationally cause issues.
        // ref https://github.com/erikras/redux-form/issues/621
        // Note: initialValues is undefined when un-minimizing
        // This needs to be included in the constructor, otherwise we try
        // initially to load the previous values. This can cause an issue if
        // someone has bad data in their form, because we won't clear out the
        // bad data, even with a hard reload.
        if (props.initialValues) {
            props.initialize(props.initialValues);
        }
    }

    componentDidMount() {
        // Avoids rendering one time with undefined initial values, which we have been
        // seeing occationally cause issues.
        // ref https://github.com/erikras/redux-form/issues/621
        // Note: initialValues is undefined when un-minimizing
        if (this.props.initialValues) {
            this.props.initialize(this.props.initialValues);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Normally setting `enableReinitialize` on `reduxForm()` below should work,
        // but https://github.com/erikras/redux-form/issues/2971 forces us to manually
        // reinitialize.  We don't use `reset`, because it can clear validation errors.
        if (
            nextProps.initialValues &&
            !_.isEqual(nextProps.initialValues, this.props.initialValues)
        ) {
            // If we already have some values, don't blow them away.
            const initialCreateFromScratchValues: Record<string, any> = {};
            if (this.state.temporarySubject) {
                initialCreateFromScratchValues.subject = this.state.temporarySubject;
            }

            if (this.state.temporaryHtmlBody) {
                initialCreateFromScratchValues.body = this.state.temporaryHtmlBody;
            }

            if (this.state.temporaryPlaintextBody) {
                initialCreateFromScratchValues.plaintextBody = this.state.temporaryPlaintextBody;
            }

            const modifiedInitialValues = {
                ...nextProps.initialValues,
                sequences: {
                    ...nextProps.initialValues.sequences,
                    'create-from-scratch': createFromScratchTemplate(
                        this.props.user,
                        initialCreateFromScratchValues,
                        // Don't add the signature, if we have a plaintext body,
                        // as it should already be in the body
                        !this.state.temporaryPlaintextBody
                    ),
                },
            } as const;

            this.props.initialize(modifiedInitialValues);

            // Clear out temporary values now that our other content has loaded
            this.setState({
                hasLoaded: true,
                temporarySubject: null,
                temporaryHtmlBody: null,
                temporaryPlaintextBody: null,
            });
        }

        this.setState({shouldShowPlaceholderWarning: hasPlaceholderErrors(nextProps)});
    }

    render() {
        return (
            <form
                className={
                    this.props.hideTemplates ? styles['container--no-templates'] : styles.container
                }
            >
                <div className={styles['container-horizontal']}>
                    <div className={styles['form-body']}>
                        {this.props.hideTemplates ? undefined : (
                            <TemplateSidebar
                                // @ts-expect-error
                                user={this.props.user}
                                errorMessage={this.props.templateListErrorMessage}
                                isLoading={this.props.isLoadingTemplates}
                                selectedSequenceId={this.props.selectedSequenceId}
                                selectedTemplateId={this.props.selectedTemplateId}
                                onTemplateSelect={this.handleTemplateSelect}
                                onCreateFromScratch={() => {
                                    this.handleTemplateSelect(
                                        CREATE_FROM_SCRATCH_ID,
                                        CREATE_FROM_SCRATCH_ID
                                    );
                                }}
                                searchValue={this.props.searchValue}
                                setSearchValue={this.props.setSearchValue}
                                closeModal={this.props.closeModal}
                                relatedLeads={this.props.relatedLeads}
                                replyQuotedSuffixHtml={this.props.replyQuotedSuffixHtml}
                            />
                        )}
                        <div className={styles['form-container']}>
                            <EmailComposerForm
                                user={this.props.user}
                                emails={this.props.relatedEmailAddresses.all}
                                relatedLeads={this.props.relatedLeads}
                                isBodyFocused={this.props.isBodyFocused}
                                isMultiSequence={this.templateCountForCurrentSequence() >= 2}
                                selectedSequenceId={this.props.selectedSequenceId}
                                selectedTemplateId={this.props.selectedTemplateId}
                                resetSelectedSequence={() => {
                                    this.props.selectSequenceId(CREATE_FROM_SCRATCH_ID);
                                }}
                                onChangeSubject={this.handleChangeSubject}
                                onChangeBody={this.handleChangeBody}
                                emailReplyId={this.props.emailReplyId}
                                suggestedReplies={this.props.suggestedReplies}
                                selectedSuggestedReplyId={this.props.suggestedEmailReplyId}
                                isLoadingSuggestedReplies={this.props.isLoadingSuggestedReplies}
                                replyQuotedSuffixHtml={this.props.replyQuotedSuffixHtml}
                            />
                            {this.props.error ? (
                                <FlashBanner
                                    type='error'
                                    onHidden={() => this.props.change('_error', null)}
                                >
                                    {this.props.error}
                                </FlashBanner>
                            ) : undefined}
                        </div>
                    </div>
                </div>
                <FooterBar
                    center={this.renderFooterCenter()}
                    right={
                        <SaveButton
                            // We want to use the saveButton, but don't want it to actually submit,
                            // because we have some confirmation logic that needs to run first.
                            type='button'
                            // @ts-expect-error
                            getButtonRef={(node) => {
                                this.saveButton = node;
                            }}
                            buttonText={this.submitButtonText()}
                            savingText='Sending'
                            successText='Sent!'
                            // @ts-expect-error
                            onClick={this.handleSaveClick}
                            onClearSaveComplete={this.handleSaveComplete}
                            errorSaving={Boolean(this.props.error)}
                            isSaving={this.props.submitting}
                        />
                    }
                />
                {this.renderDialog()}
            </form>
        );
    }

    handleSaveComplete = () => {
        const emailId = this.state.emailIdToSaveAsTemplate;

        if (emailId) {
            // Full page load, client routing is too fast, feels weird
            // @ts-expect-error
            window.location = `/email/templates#/templates/new?sentEmailId=${emailId}`;
        }
    };

    renderDialog() {
        if (
            this.state.shouldShowPlaceholderWarning &&
            !this.state.hasAcknowledgedPlaceholderWarning
        ) {
            return (
                <Dialog
                    question={PLACEHOLDER_DISABLE_TEXT}
                    isWarning={true}
                    anchor={this.saveButton}
                    primaryText='Ok'
                    onPrimary={this.handlePlaceholderAcknowledgement}
                />
            );
        }

        if (this.state.isShowingDelayError) {
            return (
                <Dialog
                    question='Something went wrong sending the email sequence. Please contact support if this issue persists.'
                    isError={true}
                    anchor={this.saveButton}
                    primaryText='Ok'
                    onPrimary={this.handleDelayErrorDismiss}
                />
            );
        }

        if (this.state.isWarningAboutSyncedEmail) {
            return (
                <Dialog
                    question={SYNC_WARNING}
                    isWarning={true}
                    anchor={this.saveButton}
                    primaryText='Continue sending'
                    secondaryText='Cancel'
                    onPrimary={this.handleWarningConfirm}
                    onSecondary={this.handleWarningCancel}
                />
            );
        }

        return null;
    }

    renderFooterCenter = () => {
        if (this.props.selectedSequenceId !== CREATE_FROM_SCRATCH_ID || this.props.isReply) {
            return null;
        }

        return (
            <div className={styles['create-template']}>
                <Checkbox
                    isLarge={true}
                    checked={this.state.shouldSaveAsTemplate}
                    label='Save as template after sending'
                    onChange={() => {
                        this.setState((prevState) => ({
                            shouldSaveAsTemplate: !prevState.shouldSaveAsTemplate,
                        }));
                    }}
                />
            </div>
        );
    };

    handleChangeBody = ({plaintext, html}: {plaintext: string; html: string}) => {
        const fieldName = getTemplateFormStateName({
            sequenceId: this.props.selectedSequenceId,
            templateId: this.props.selectedTemplateId,
        });
        this.props.change(`${fieldName}.plaintextBody`, plaintext);

        if (!this.state.hasLoaded) {
            this.setState({
                temporaryPlaintextBody: plaintext,
                temporaryHtmlBody: html,
            });
        }
        this.props.onEmailEdited();
    };

    handleChangeSubject = (newSubject: string) => {
        if (!this.state.hasLoaded) {
            this.setState({
                temporarySubject: newSubject,
            });
        }
    };

    handleTemplateSelect = (selectedSequenceId: string, selectedTemplateId: string) => {
        this.props.selectSequenceId(selectedSequenceId);
        this.props.selectTemplateId(selectedTemplateId);
    };

    /*
     * If the user is queueing up a sequence, but they don't have sync enabled, let's
     */
    handleSaveClick = () => {
        // Reset acknowledgement bit for placeholder warning on new click
        this.setState({hasAcknowledgedPlaceholderWarning: false});

        if (!this.props.isSyncEnabled && this.templateCountForCurrentSequence() > 1) {
            this.setState({isWarningAboutSyncedEmail: true});

            return;
        }

        if (this.state.shouldShowPlaceholderWarning) {
            return;
        }

        this.props.handleSubmit(this.handleSubmit)();
    };

    handleWarningConfirm = () => {
        this.setState({
            isWarningAboutSyncedEmail: false,
        });

        this.props.handleSubmit(this.handleSubmit)();
    };

    handleWarningCancel = () => {
        this.setState({
            isWarningAboutSyncedEmail: false,
        });
    };

    handlePlaceholderAcknowledgement = () => {
        this.setState({
            hasAcknowledgedPlaceholderWarning: true,
        });
    };

    handleDelayErrorDismiss = () => {
        this.setState({
            isShowingDelayError: false,
        });
    };

    handleSubmit = (values: EmailComposerFormStateNew) => {
        // Avoid double-submitting
        if (this.props.submitting || this.props.submitSucceeded) return Promise.resolve();
        const shouldCreateTemplate =
            this.state.shouldSaveAsTemplate &&
            this.props.selectedSequenceId === CREATE_FROM_SCRATCH_ID;

        return (
            this.props
                .onSubmit({
                    form: values,
                    sequenceId: this.props.selectedSequenceId,
                    shouldNotify: !shouldCreateTemplate,
                })
                // @ts-expect-error
                .then((email) => {
                    if (email && shouldCreateTemplate) {
                        this.setState({
                            emailIdToSaveAsTemplate: email.id,
                        });
                    } else {
                        this.props.closeModal();
                    }

                    // Sending an email can be a stage goal requiring a refetch
                    // to update the stage of a lead, and peep pages need email
                    // widget/engagment states updated
                    refetchForEvent('emailSent');
                    accountRefetchForEvent('emailSent');
                    contactRefetchForEvent('emailSent');
                })
                .catch((err: GraphQLErrorObject) => {
                    if (err.message === DELAY_ERROR) {
                        this.setState({
                            isShowingDelayError: true,
                        });

                        return;
                    }

                    const errorMessage = safelyGetGraphQLErrorMessage(err);

                    // @see https://redux-form.com/6.7.0/examples/submitvalidation/
                    throw new SubmissionError({
                        _error: errorMessage,
                    });
                })
        );
    };

    submitButtonText(): string {
        const templateCount = this.templateCountForCurrentSequence();

        // If there's only one, or hasn't loaded yet, we'll just say "Send".
        if (templateCount < 2) {
            return 'Send';
        }

        // We count all but the first email in a sequence as "follow-ups"
        const followUps = templateCount - 1;
        const followUpsText = followUps === 1 ? '1 follow-up' : `${followUps} follow-ups`;

        return `Send now & schedule ${followUpsText}`;
    }

    templateCountForCurrentSequence(): number {
        if (this.props.sequences) {
            const sequence = this.props.sequences[this.props.selectedSequenceId];

            return sequence ? Object.keys(sequence.emailTemplates).length : 0;
        }

        return 0;
    }
}

// This ugly stinky little pile of code is used to divine from redux-form
// whether the body field-validation for custom placeholders is being
// failed for any of the templates in the selected sequence.
function hasPlaceholderErrors(props: Props): boolean {
    const selectedSequenceId = props.selectedSequenceId;
    if (
        props.submitFailed &&
        props.syncErrors.sequences &&
        props.syncErrors.sequences[selectedSequenceId] &&
        props.syncErrors.sequences[selectedSequenceId].emailTemplates
    ) {
        const templateErrorMessages = Object.values(
            props.syncErrors.sequences[selectedSequenceId].emailTemplates
        ).map((templateErrors: any) => templateErrors.body);

        if (templateErrorMessages.includes(PLACEHOLDER_DISABLE_TEXT)) {
            return true;
        }
    }

    return false;
}

// @ts-expect-error
const connector = connect<Props, OwnAndFormProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
const EmailSenderFormConnected = connector(EmailSenderFormComponent);

function validate(values: EmailComposerFormStateNew) {
    const errors: Record<string, any> = {};

    if (!values.toAddresses || !values.toAddresses.length) {
        // No recipent address
        errors.toAddresses = 'Who should this be sent to?';
    }

    return errors;
}

const withReduxForm = reduxForm({
    form: FORM_NAME,
    destroyOnUnmount: false,
    keepDirtyOnReinitialize: true,
    validate,
});

export const EmailSenderFormNew = withReduxForm(EmailSenderFormConnected);
